import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import api from '../services/api';
import { basculer } from './ventesUtils';
import FiltresPanel from './FiltresPanel';
import './Rapport.css';

// ─── Gabarit de page Rapport ─────────────────────────────────────────────────
// Anatomie constante : titre → tuiles KPI → graphiques → tableau de détail,
// avec le panneau de filtres à droite (repliable, façon Power BI). L'état
// (période, sociétés) vit dans l'URL : un rapport filtré se partage par
// copier-coller de lien. Données FICTIVES tant que les sources réelles ne
// sont pas branchées (bandeau explicite).

const COULEURS = ['#2E5BFF', '#C9A84C', '#0F766E', '#9D174D', '#4D7C0F', '#B45309', '#475569'];

const eur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const eurCourt = (v) => (Math.abs(v) >= 1000000 ? `${(v / 1000000).toFixed(1)} M€` : `${Math.round(v / 1000)} k€`);
// Un rapport de stock compte des véhicules, pas des euros : l'axe et les
// infobulles suivent l'unité annoncée par le serveur.
const nombreFr = (v) => new Intl.NumberFormat('fr-FR').format(Math.round(v || 0));
const moisLabel = (ym) => {
  const [a, m] = ym.split('-');
  return new Date(a, m - 1, 1).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
};

function Rapport() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  // Le panneau se souvient d'être ouvert ou replié (préférence par navigateur).
  const [panneauOuvert, setPanneauOuvert] = useState(() => {
    try { return localStorage.getItem('bi-filtres-ouverts') !== 'non'; } catch (e) { return true; }
  });

  const mois = parseInt(params.get('mois') || '12', 10);
  const societesUrl = params.get('societes') || '';

  useEffect(() => {
    let actif = true;
    setErreur('');
    api.get(`/bi/rapports/${slug}/data`, { params: { mois, societes: societesUrl } })
      .then((res) => { if (actif) setData(res.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [slug, mois, societesUrl]);

  const togglePanneau = () => {
    setPanneauOuvert((o) => {
      try { localStorage.setItem('bi-filtres-ouverts', o ? 'non' : 'oui'); } catch (e) { /* sans gravité */ }
      return !o;
    });
  };

  const setFiltre = (cles) => {
    const next = new URLSearchParams(params);
    Object.entries(cles).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  const toggleSociete = (code) => {
    const actives = data.societes;
    const suivantes = actives.includes(code)
      ? actives.filter((c) => c !== code)
      : [...actives, code];
    // Tout décocher = tout afficher (jamais un graphique vide par accident).
    setFiltre({ societes: suivantes.length === 0 || suivantes.length === data.societesDispo.length ? '' : suivantes.join(',') });
  };

  const couleurDe = useMemo(() => {
    const m = {};
    (data?.societesDispo || []).forEach((s, i) => { m[s.code] = COULEURS[i % COULEURS.length]; });
    return m;
  }, [data?.societesDispo]);

  // Filtres actifs = écarts par rapport aux défauts (12 mois, toutes sociétés).
  const nbActifs = (mois !== 12 ? 1 : 0) + (societesUrl ? 1 : 0);

  const nomDe = (code) => data?.societesDispo.find((s) => s.code === code)?.nom || code;
  const k = data?.kpis;

  return (
    <>
      <main className="rp-page">
        {erreur ? (
          <div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div>
        ) : (
          <>
            <div className="rp-head">
              <div className="rp-titre-bloc">
                <h1>{data ? data.rapport.titre : ' '}</h1>
                <span className="rp-contexte">
                  {data && `${data.rapport.metier_nom} · ${data.rapport.branche_nom || 'Toutes branches'}`}
                </span>
              </div>
              {data?.fictif && <span className="rp-fictif">Données fictives</span>}
            </div>

            {/* Une seule décision de format pour tout l'écran : un rapport de
                stock compte des véhicules, pas des euros. */}
            {!data ? (
              <div className="rp-squelette">
                <div className="rp-kpis">{[0, 1, 2, 3].map((i) => <div key={i} className="rp-kpi sq" />)}</div>
                <div className="rp-carte sq" style={{ height: 320 }} />
              </div>
            ) : (
              <>
                {/* Un rapport réel envoie ses propres cartes : un stock mélange
                    des véhicules, des euros, des jours et un pourcentage — les
                    quatre cases génériques, toutes en euros, mentiraient. */}
                <div className="rp-kpis">
                  {k.cartes ? k.cartes.map((c, i) => (
                    <div className="rp-kpi" key={i}>
                      <div className="rp-kpi-label">{c.label}</div>
                      <div className={`rp-kpi-valeur${/^-/.test(c.valeur) ? ' negatif' : (/^\+/.test(c.valeur) ? ' positif' : '')}`}>{c.valeur}</div>
                      {c.sous && <div className="rp-kpi-sous">{c.sous}</div>}
                    </div>
                  )) : (
                    <>
                      <div className="rp-kpi">
                        <div className="rp-kpi-label">Total sur la période</div>
                        <div className="rp-kpi-valeur">{eur(k.total)}</div>
                      </div>
                      <div className="rp-kpi">
                        <div className="rp-kpi-label">vs période précédente</div>
                        <div className={`rp-kpi-valeur ${k.evolutionPct >= 0 ? 'positif' : 'negatif'}`}>
                          {k.evolutionPct >= 0 ? '+' : ''}{k.evolutionPct} %
                        </div>
                      </div>
                      <div className="rp-kpi">
                        <div className="rp-kpi-label">Moyenne mensuelle</div>
                        <div className="rp-kpi-valeur">{eur(k.moyenneMensuelle)}</div>
                      </div>
                      <div className="rp-kpi">
                        <div className="rp-kpi-label">Meilleure société</div>
                        <div className="rp-kpi-valeur">{k.meilleureSociete ? k.meilleureSociete.nom : '—'}</div>
                        {k.meilleureSociete && <div className="rp-kpi-sous">{eur(k.meilleureSociete.total)}</div>}
                      </div>
                    </>
                  )}
                </div>

                <div className="rp-carte">
                  <h2 className="rp-carte-titre">
                    {data.unite === 'unites' ? 'Entrées en stock par mois et par société' : 'Évolution mensuelle par société'}
                  </h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.serie} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default, #e2e8f0)" />
                      <XAxis dataKey="mois" tickFormatter={moisLabel} fontSize={12} />
                      <YAxis tickFormatter={data.unite === 'unites' ? nombreFr : eurCourt} fontSize={12} width={64} />
                      <Tooltip formatter={(v, nom) => [data.unite === 'unites' ? `${nombreFr(v)} véh.` : eur(v), nomDe(nom)]} labelFormatter={moisLabel} />
                      <Legend formatter={nomDe} />
                      {data.societes.map((code) => (
                        <Line key={code} type="monotone" dataKey={code} stroke={couleurDe[code]} strokeWidth={2} dot={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="rp-deux-colonnes">
                  <div className="rp-carte">
                    <h2 className="rp-carte-titre">Total par société</h2>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={data.totauxSociete} layout="vertical" margin={{ top: 4, right: 24, bottom: 0, left: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border-default, #e2e8f0)" />
                        <XAxis type="number" tickFormatter={data.unite === 'unites' ? nombreFr : eurCourt} fontSize={12} />
                        <YAxis type="category" dataKey="nom" width={130} fontSize={12} />
                        <Tooltip formatter={(v) => (data.unite === 'unites' ? `${nombreFr(v)} véh.` : eur(v))} />
                        <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                          {data.totauxSociete.map((t) => (
                            <Cell key={t.code} fill={couleurDe[t.code]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="rp-carte rp-table-carte">
                    <h2 className="rp-carte-titre">Détail mensuel</h2>
                    <div className="rp-table-scroll">
                      <table className="rp-table">
                        <thead>
                          <tr>
                            <th>Mois</th>
                            {data.societes.map((code) => (
                              <th key={code} className={`tbl-clic${societesUrl === code ? ' actif' : ''}`}
                                  title="Cliquez pour n'afficher que cette société"
                                  onClick={() => setFiltre({ societes: basculer(societesUrl, code) })}>{nomDe(code)}</th>
                            ))}
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...data.serie].reverse().map((p) => (
                            <tr key={p.mois}>
                              <td>{moisLabel(p.mois)}</td>
                              {data.societes.map((code) => <td key={code}>{data.unite === 'unites' ? nombreFr(p[code]) : eur(p[code])}</td>)}
                              <td className="rp-total">
                                {data.unite === 'unites'
                                  ? nombreFr(data.societes.reduce((t, c) => t + p[c], 0))
                                  : eur(data.societes.reduce((t, c) => t + p[c], 0))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>

      <FiltresPanel
        ouvert={panneauOuvert}
        onToggle={togglePanneau}
        mois={mois}
        onMois={(m) => setFiltre({ mois: String(m) })}
        societesDispo={data?.societesDispo || []}
        societesActives={data?.societes || []}
        onToggleSociete={toggleSociete}
        onToutesSocietes={() => setFiltre({ societes: '' })}
        onAucuneSociete={() => setFiltre({ societes: '__aucune__' })}
        couleurDe={couleurDe}
        nbActifs={nbActifs}
        onReset={() => setFiltre({ mois: '', societes: '' })}
      />
    </>
  );
}

export default Rapport;
